/**
 * BFF session helpers.
 *
 * Provides:
 * - probeBffUser()     — fetch /bff/authinfo to check session validity and return identity
 * - fetchBffToken()    — fetch /bff/token to obtain a bridged access token (in-memory only)
 * - redirectToBffLogin()  — restart sign-in via /bff/login on 401/unauthenticated
 * - redirectToBffLogout() — log out via /bff/logout (clears the server-side session)
 *
 * All calls use credentials:'include' so the HttpOnly BFF session cookie is
 * automatically attached. The X-CSRF:'1' header satisfies the BFF anti-forgery
 * check on state-mutating endpoints (the probe is read-only so it does not
 * require CSRF, but the token endpoint does).
 *
 * The bridge token returned by fetchBffToken() is held in-memory by the
 * caller (AuthModule.getCurrentToken). It is NEVER written to
 * sessionStorage, localStorage, or any JS-readable cookie.
 */

import environment from '@lib/environment';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single claim from the BFF /bff/authinfo response. Hidden claims have a
 *  null value (the BFF redacts them), so value is nullable. */
export interface BffUserClaim {
  type: string;
  value: string | null;
}

/** Shape of the ServiceTitan.Auth.Bff `/bff/authinfo` response. */
interface BffAuthInfoResponse {
  isAuthenticated: boolean;
  info?: {
    subject?: string;
    idp?: string;
    name?: string | null;
    userName?: string | null;
    sessionId?: string;
  };
  claims?: BffUserClaim[];
}

export interface BffUserResponse {
  /** All claims returned by the BFF /bff/authinfo endpoint. */
  claims: BffUserClaim[];
  /** Convenience: subject (user id from IdP). */
  sub: string;
  /** Convenience: display name (may be empty — often null at this stage). */
  name: string;
  /** Server-side session id — required as the `sid` CSRF guard on /bff/logout. */
  sessionId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function claimValue(claims: BffUserClaim[], type: string): string {
  return claims.find((c) => c.type === type)?.value ?? '';
}

function bffUrl(path: string): string {
  // The BFF lives at the same origin as the SPA (API_URL is a same-site sibling).
  // Strip any trailing slash from API_URL to avoid double-slashes.
  const base = environment.API_URL.replace(/\/$/, '');
  return `${base}${path}`;
}

// ---------------------------------------------------------------------------
// /bff/authinfo probe
// ---------------------------------------------------------------------------

/**
 * Probes the BFF to determine whether the server-side session is valid.
 *
 * Uses ServiceTitan.Auth.Bff's `/bff/authinfo` endpoint, which returns
 * `{ isAuthenticated, info, claims }`. (The Duende-stock `/bff/user` endpoint
 * is not wired in this BFF and returns 401.)
 *
 * Returns the parsed identity when `isAuthenticated === true`. Returns null when
 * the session is not established (non-OK response, or isAuthenticated false), so
 * callers can branch on `null` to trigger a BFF login redirect.
 *
 * Does NOT throw on HTTP errors — callers receive `null` and decide what to do.
 */
export async function probeBffUser(): Promise<BffUserResponse | null> {
  try {
    const response = await fetch(bffUrl('/bff/authinfo'), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-CSRF': '1',
      },
    });

    if (!response.ok) {
      return null;
    }

    const body = (await response.json()) as BffAuthInfoResponse;
    if (!body.isAuthenticated) {
      return null;
    }

    const claims = body.claims ?? [];
    return {
      claims,
      sub: body.info?.subject ?? claimValue(claims, 'sub'),
      name:
        body.info?.name ?? body.info?.userName ?? claimValue(claims, 'name'),
      sessionId: body.info?.sessionId ?? claimValue(claims, 'sid'),
    };
  } catch {
    // Network failure — treat as unauthenticated
    return null;
  }
}

// ---------------------------------------------------------------------------
// /bff/token endpoint (bridge)
// ---------------------------------------------------------------------------

/**
 * Fetches a bridged access token from the BFF token endpoint.
 *
 * The token is returned as a plain string. Callers (getCurrentToken) hold it
 * in-memory ONLY — it must never be written to any persistent storage.
 *
 * Returns the access token string on success. Throws on error so the caller
 * can propagate the failure the same way a PKCE token refresh failure would.
 */
export async function fetchBffToken(): Promise<string> {
  const response = await fetch(bffUrl('/bff/token'), {
    method: 'GET',
    credentials: 'include',
    headers: {
      'X-CSRF': '1',
    },
  });

  if (!response.ok) {
    throw new Error(`BFF token endpoint returned ${response.status}`);
  }

  const body = (await response.json()) as { access_token?: string };
  const token = body.access_token;
  if (!token) {
    throw new Error('BFF token endpoint returned no access_token');
  }

  return token;
}

// ---------------------------------------------------------------------------
// 401 restart
// ---------------------------------------------------------------------------

/**
 * Restarts sign-in by navigating to /bff/login.
 *
 * Called when a BFF-backed API call returns 401/unauthenticated, covering the
 * stale-tab and cross-tab-logout cases. The page navigates top-level (no
 * PKCE/token-refresh attempt) so the BFF can re-establish the session.
 *
 * @param returnUrl - absolute URL to return to after sign-in; defaults to the
 *   full current SPA URL.
 */
export function redirectToBffLogin(returnUrl?: string): void {
  // The SPA and BFF are on different origins by design. returnUrl MUST be the
  // FULL SPA URL (e.g. http://localhost:5200/session-signin) — a relative path
  // would be resolved by the BFF against its own origin and land the user back
  // on the API host. window.location.href is the absolute current URL.
  const target = returnUrl ?? window.location.href;
  // /bff/login lives on the API origin (API_URL), same as the /bff/user probe.
  window.location.href = `${bffUrl('/bff/login')}?returnUrl=${encodeURIComponent(
    target,
  )}`;
}

// ---------------------------------------------------------------------------
// BFF logout
// ---------------------------------------------------------------------------

/**
 * Logs the user out via the BFF (session path).
 *
 * Navigates top-level to /bff/logout so the BFF clears its server-side session
 * (cookie + token store) and drives the upstream OIDC end-session. Do NOT hit
 * the IdP's /connect/endsession directly on the session path — that's the
 * PKCE/browser logout and would leave the BFF session cookie intact.
 *
 * Duende-style /bff/logout requires the session id (`sid`) as a logout-CSRF
 * guard; we read it from /bff/authinfo (the cookie is still valid at logout
 * time) and pass it through. The `returnUrl` is the absolute SPA URL the BFF
 * sends the browser back to once logout completes (defaults to the SPA root).
 *
 * @param returnUrl - absolute SPA URL to land on after logout; defaults to the
 *   SPA origin root.
 */
export async function redirectToBffLogout(returnUrl?: string): Promise<void> {
  const target = returnUrl ?? `${window.location.origin}/`;

  let sidParam = '';
  try {
    const session = await probeBffUser();
    if (session?.sessionId) {
      sidParam = `sid=${encodeURIComponent(session.sessionId)}&`;
    }
  } catch {
    // best-effort — proceed without sid (BFF may still accept cookie-only logout)
  }

  window.location.href = `${bffUrl('/bff/logout')}?${sidParam}returnUrl=${encodeURIComponent(
    target,
  )}`;
}
